import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Tab, Tabs } from '@mui/material';
import { InsightsOutlined } from '@mui/icons-material';
import { IconTile, ListSkeleton, PageContainer, PageHeader } from '../pagekit';
import { RouteLoading } from '../ProtectedRoute';
import NotFound from '../NotFound';
import { callPlatformAdmin, isConsoleDenied } from './platformAdminApi';
import ConsoleOverview from './ConsoleOverview';
import ConsoleTenants from './ConsoleTenants';
import ConsoleIsolation from './ConsoleIsolation';
import TenantDrawer from './TenantDrawer';
import ConsoleCampaigns from './ConsoleCampaigns';

const TABS = [
  { key: 'overview', label: 'Overview', action: 'overview' },
  { key: 'tenants', label: 'Tenants', action: 'tenants' },
  { key: 'isolation', label: 'Isolation', action: 'isolation_checks' },
  // Campaigns loads its own data from the marketing-campaigns function (no platform-admin action).
  { key: 'campaigns', label: 'Campaigns', action: null },
];

const IDLE = { status: 'idle', data: null, error: null };

/**
 * Platform console (owner only): tenants, users per tenant, tiers and tenant-isolation health.
 * Authorization is server side (platform-admin answers 404 to everyone else); on a 404 this
 * page renders the regular not-found page so it does not reveal itself.
 * @returns {JSX.Element} Page.
 */
export default function ConsolePage() {
  const [tab, setTab] = useState('overview');
  const [denied, setDenied] = useState(false);
  const [panels, setPanels] = useState({ overview: IDLE, tenants: IDLE, isolation: IDLE, campaigns: IDLE });
  const [selectedTenant, setSelectedTenant] = useState(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const load = useCallback((key) => {
    const spec = TABS.find((t) => t.key === key);
    if (!spec?.action) return;
    setPanels((prev) => ({ ...prev, [key]: { ...prev[key], status: prev[key].data ? 'refreshing' : 'loading', error: null } }));
    callPlatformAdmin(spec.action).then(
      (data) => {
        if (liveRef.current) setPanels((prev) => ({ ...prev, [key]: { status: 'ready', data, error: null } }));
      },
      (error) => {
        if (!liveRef.current) return;
        if (isConsoleDenied(error)) {
          setDenied(true);
          return;
        }
        setPanels((prev) => ({ ...prev, [key]: { ...prev[key], status: 'error', error: error?.message || 'Something went wrong.' } }));
      }
    );
  }, []);

  // Overview doubles as the access check: nothing of the console renders until it answers.
  useEffect(() => {
    load('overview');
  }, [load]);

  useEffect(() => {
    if (panels[tab].status === 'idle' && panels.overview.status === 'ready') load(tab);
  }, [tab, panels, load]);

  if (denied) return <NotFound />;
  const overview = panels.overview;
  if (!overview.data && (overview.status === 'idle' || overview.status === 'loading')) return <RouteLoading label="Loading console..." />;
  if (!overview.data && overview.status === 'error') {
    return (
      <PageContainer>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => load('overview')}>Retry</Button>}>
          {overview.error}
        </Alert>
      </PageContainer>
    );
  }

  const panel = panels[tab];
  const body = () => {
    if (tab === 'campaigns') return <ConsoleCampaigns />;
    if (panel.status === 'error' && !panel.data) {
      return (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => load(tab)}>Retry</Button>}>
          {panel.error}
        </Alert>
      );
    }
    if (!panel.data) return <ListSkeleton rows={6} label={`Loading ${tab}`} />;
    if (tab === 'overview') return <ConsoleOverview data={panel.data} />;
    if (tab === 'tenants') return <ConsoleTenants tenants={panel.data.tenants || []} onSelect={setSelectedTenant} />;
    return <ConsoleIsolation data={panel.data} refreshing={panel.status === 'refreshing' || panel.status === 'loading'} onRefresh={() => load('isolation')} />;
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        leading={<IconTile><InsightsOutlined /></IconTile>}
        title="Platform console"
        description="Every tenant, its users and the tiers they hold, live tenant isolation checks, and product email campaigns."
      />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', minWidth: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          aria-label="Console sections"
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: 14 } }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} id={`console-tab-${t.key}`} aria-controls={`console-panel-${t.key}`} />
          ))}
        </Tabs>
      </Box>
      {panel.status === 'error' && panel.data && (
        <Alert severity="warning">{`Could not refresh: ${panel.error}`}</Alert>
      )}
      <Box role="tabpanel" id={`console-panel-${tab}`} aria-labelledby={`console-tab-${tab}`} sx={{ minWidth: 0 }}>
        {body()}
      </Box>
      <TenantDrawer tenant={selectedTenant} onClose={() => setSelectedTenant(null)} />
    </PageContainer>
  );
}
